/**
 * <toppik-product-card>
 * Makes the branded card's option selectors functional. Each product option
 * is a row (colour as swatches, others as text pills). Selecting a value in
 * any row matches the variant across ALL selected options, then swaps the
 * image, price and colour name, and points the add-to-cart form at it.
 */
class ToppikProductCard extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('.tpc__img--primary');
    this.hoverImage = this.querySelector('.tpc__img--hover');
    this.priceEl = this.querySelector('.tpc__price');
    this.colorEl = this.querySelector('.tpc__color');
    this.variantInput = this.querySelector('input[name="id"]');
    this.atc = this.querySelector('.tpc__atc');

    // Multi-option cards (e.g. shade + size): the colour swatches only preview
    // the colour on the card (swap image + name). The "Choose" button opens the
    // quick-view drawer — clicking a swatch must NOT open it.
    const colorSelects = this.querySelectorAll('.tpc__opt[data-color-select]');
    if (colorSelects.length) {
      colorSelects.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.selectColor(btn);
          this.releaseFocus(btn);
        });
      });
    }

    // Swatch overflow: collapse to one row with a chevron that expands the card.
    this.setupSwatchToggles();
    // Preview the shade name on swatch hover (like the PDP).
    this.setupColorHover();

    const dataEl = this.querySelector('.tpc__variants');
    try {
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (e) {
      this.variants = [];
    }
    if (!this.variants.length) return;

    this.options = Array.from(this.querySelectorAll('.tpc__option'));
    // Which option index holds the colour (for updating the colour label)
    const colorRow = this.querySelector('.tpc__option[data-color-option]');
    this.colorIndex = colorRow ? Number(colorRow.dataset.optionIndex) : -1;

    // Current selection: one value per option, seeded from aria-pressed
    this.selected = this.options.map((row) => {
      const active = row.querySelector('.tpc__opt[aria-pressed="true"]') || row.querySelector('.tpc__opt');
      return active ? active.dataset.value : null;
    });

    this.querySelectorAll('.tpc__opt').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.choose(btn);
        this.releaseFocus(btn);
      });
    });
  }

  /**
   * After a POINTER selection, drop focus so the card's :focus-within hover
   * state (outline + revealed add-to-cart) doesn't stick until the user clicks
   * away. Keyboard selections keep focus (they are :focus-visible).
   */
  releaseFocus(btn) {
    if (!btn.matches(':focus-visible')) btn.blur();
  }

  /**
   * Colour swatches collapse to a single row. When they overflow, reveal a
   * chevron that expands the card to show every shade (the grid keeps sibling
   * cards the same height). Chevron is hidden when everything already fits.
   */
  setupSwatchToggles() {
    this.querySelectorAll('.tpc__option--swatch').forEach((row) => {
      const swatches = row.querySelector('[data-swatches]');
      const toggle = row.querySelector('[data-swatch-toggle]');
      if (!swatches || !toggle) return;

      const sync = () => {
        if (row.classList.contains('is-expanded')) {
          toggle.hidden = false;
          return;
        }
        // Overflowing when the swatches are taller than the collapsed one row.
        toggle.hidden = swatches.scrollHeight <= swatches.clientHeight + 1;
      };

      requestAnimationFrame(sync);
      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(row);
      } else {
        window.addEventListener('resize', sync);
      }

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const expanded = row.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        // Drop focus after a pointer click so the card's :focus-within hover
        // state doesn't stick (keyboard keeps focus via :focus-visible).
        this.releaseFocus(toggle);
      });
    });
  }

  /**
   * Preview the shade name in the colour label while hovering a swatch, and
   * restore the selected shade's name on leave. Only on devices with real
   * hover, so a tap on touch just selects (avoiding sticky hover).
   */
  setupColorHover() {
    if (!this.colorEl) return;
    if (!(window.matchMedia && window.matchMedia('(hover: hover)').matches)) return;
    const swatches = this.querySelectorAll('.tpc__swatch');
    if (!swatches.length) return;
    swatches.forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        if (btn.dataset.value) this.colorEl.textContent = btn.dataset.value;
      });
      btn.addEventListener('mouseleave', () => this.restoreColorName());
    });
  }

  /** Set the colour label back to the currently selected shade. */
  restoreColorName() {
    if (!this.colorEl) return;
    const row = this.querySelector('.tpc__option[data-color-option]');
    const active = row?.querySelector('.tpc__opt[aria-pressed="true"]') || row?.querySelector('.tpc__opt');
    if (active?.dataset.value) this.colorEl.textContent = active.dataset.value;
  }

  /** Multi-option card: preview a colour (pressed state + name + image). */
  selectColor(btn) {
    const row = btn.closest('.tpc__option');
    if (row) {
      row.querySelectorAll('.tpc__opt').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    }
    if (this.colorEl && btn.dataset.value) this.colorEl.textContent = btn.dataset.value;
    // Carry the chosen colour into the quick view when it opens.
    if (btn.dataset.value) {
      this.querySelector('[data-quick-view-open]')?.setAttribute('data-selected-color', btn.dataset.value);
    }
    const media = btn.dataset.media;
    if (media) {
      [this.image, this.hoverImage].forEach((img) => {
        if (!img) return;
        img.removeAttribute('srcset');
        img.src = media;
      });
    }
  }

  choose(btn) {
    const index = Number(btn.dataset.optionIndex);
    this.selected[index] = btn.dataset.value;

    // Toggle pressed state within this option row only
    const row = this.options[index];
    if (row) {
      row.querySelectorAll('.tpc__opt').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    }

    this.update();
  }

  /** Variant whose options match every current selection. */
  findVariant() {
    return this.variants.find((v) => v.options && this.selected.every((val, i) => v.options[i] === val)) || null;
  }

  update() {
    // Colour label follows the colour option
    if (this.colorIndex > -1 && this.colorEl && this.selected[this.colorIndex]) {
      this.colorEl.textContent = this.selected[this.colorIndex];
    }

    const variant = this.findVariant();
    if (!variant) {
      // No such combination — nothing to add
      if (this.atc) this.atc.disabled = true;
      return;
    }

    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.toggleAttribute('disabled', !variant.available);
    }

    if (variant.media) {
      [this.image, this.hoverImage].forEach((img) => {
        if (!img) return;
        img.removeAttribute('srcset');
        img.src = variant.media;
      });
    }

    if (this.priceEl) {
      const amountEl = this.priceEl.querySelector('[data-tpc-amount]');
      if (amountEl && variant.price) amountEl.textContent = variant.price;

      const compareEl = this.priceEl.querySelector('[data-tpc-compare]');
      if (compareEl) {
        if (variant.compare) {
          compareEl.textContent = variant.compare;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    if (this.atc) this.atc.disabled = !variant.available;
  }
}

if (!customElements.get('toppik-product-card')) {
  customElements.define('toppik-product-card', ToppikProductCard);
}
